import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { db, ensureAuthenticated, auth, functions } from "../firebase";
import { collection, getDocs, query, orderBy, deleteDoc, doc, where, getDoc, updateDoc, onSnapshot, serverTimestamp } from "firebase/firestore";
import { ref, deleteObject, getStorage } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { motion, AnimatePresence } from "framer-motion";
import { migrateUserData } from "../utils/migrateData";
import { X, StickyNote, ImageIcon, Clock } from 'lucide-react';
import Header from "../components/Header";
import Button from "../components/Button";
import Icon from "../components/Icon";
import MemoryNoteCard from "../components/MemoryNoteCard";
import { useAdminMode } from "../context/AdminModeContext";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { formatDate } from "../utils/textHelpers";
import { useMemorial } from "../hooks/useMemorial";

export default function MyUploads() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [activeTab, setActiveTab] = useState("notes");
  const [notes, setNotes] = useState([]);
  const [images, setImages] = useState([]);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState("");
  const [discardConfirm, setDiscardConfirm] = useState(false);
  const touchStartPos = useRef({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const imgRef = useRef(null);
  const { adminMode, setAdminMode } = useAdminMode();
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);

  // Memorial data and ownership come from the shared hook
  const { memorial, isOwner } = useMemorial(slug);

  // Track which pending submission IDs have already had their preview URL
  // fetched. Using a ref avoids the stale-closure issue that previously
  // required an eslint-disable comment on the preview fetch effect.
  const fetchedPreviewIdsRef = useRef(new Set());

  // Admin specific state
  const [pending, setPending] = useState([]);
  const [previewUrlsById, setPreviewUrlsById] = useState({});

  // Watch auth state only for the login/logout UI affordance.
  // Ownership is fully handled by useMemorial.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setIsAdminLoggedIn(!!u && !u.isAnonymous && !!u.email);
    });
    return () => unsub();
  }, []);

  // Run the legacy data migration exactly once on mount.
  useEffect(() => {
    migrateUserData().catch(console.error);
  }, []);

  // Re-fetch content whenever the active tab changes.
  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        await fetchContent();
      } catch (err) {
        console.error("Error fetching content:", err);
        setError("Failed to fetch content. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  // fetchContent is defined below; ESLint wants it in deps but it's stable
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Admin: Fetch pending submissions
  useEffect(() => {
    if (!isOwner || !memorial?.id) return;

    const sref = collection(db, "memorials", memorial.id, "submissions");
    const unsub = onSnapshot(query(sref, where("status", "==", "pending")), (s) => {
      const items = s.docs.map(x => ({ id: x.id, ...x.data() }));
      // Avoid composite-index requirement by sorting client-side.
      items.sort((a, b) => {
        const aMs = a?.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const bMs = b?.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return aMs - bMs;
      });
      setPending(items);
    });
    return () => unsub();
  }, [isOwner, memorial?.id]);

  // Admin: Fetch signed preview URLs for pending image submissions.
  // fetchedPreviewIdsRef tracks which IDs we've already fetched so we
  // never call the Cloud Function twice for the same submission, even
  // if `pending` or `previewUrlsById` change between renders.
  useEffect(() => {
    const fetchPreviews = async () => {
      if (!memorial?.id || pending.length === 0) return;
      const getPreview = httpsCallable(functions, "getPendingPreviewUrl");
      const updates = {};
      for (const p of pending) {
        if (
          p.type === "image" &&
          p.tempImagePath &&
          !fetchedPreviewIdsRef.current.has(p.id)
        ) {
          try {
            const res = await getPreview({
              memorialId: memorial.id,
              tempImagePath: p.tempImagePath,
            });
            if (res?.data?.url) {
              updates[p.id] = res.data.url;
              fetchedPreviewIdsRef.current.add(p.id);
            }
          } catch (e) {
            // Silently ignore — preview will show a placeholder icon
          }
        }
      }
      if (Object.keys(updates).length > 0) {
        setPreviewUrlsById((prev) => ({ ...prev, ...updates }));
      }
    };
    fetchPreviews();
  }, [pending, memorial?.id]);

  const fetchContent = async () => {
    setSelectedItems(new Set());
    setIsSelectionMode(false);
    setIsLoading(true);
    setError(null);
    try {
      const user = await ensureAuthenticated();
      
      const notesQuery = query(
        collection(db, "notes"),
        where("userId", "==", user.uid),
        orderBy("createdAt", "desc")
      );
      const notesSnapshot = await getDocs(notesQuery);
      const notesData = notesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const imagesQuery = query(
        collection(db, "images"),
        where("userId", "==", user.uid),
        orderBy("createdAt", "desc")
      );
      const imagesSnapshot = await getDocs(imagesQuery);
      const imagesData = imagesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setNotes(notesData);
      setImages(imagesData);
    } catch (error) {
      console.error("Error fetching content:", error);
      setError("Failed to fetch content. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const [processingIds, setProcessingIds] = useState(new Set());
  const [moderationToast, setModerationToast] = useState(null);

  useEffect(() => {
    if (!moderationToast) return;
    const t = setTimeout(() => setModerationToast(null), 2200);
    return () => clearTimeout(t);
  }, [moderationToast]);

  // Admin Actions
  const updateStatus = async (id, status) => {
    if (!memorial?.id) return;
    setProcessingIds(prev => new Set(prev).add(id));
    try {
      await updateDoc(doc(db, "memorials", memorial.id, "submissions", id), {
        status,
        moderatedAt: serverTimestamp()
      });
      // Remove immediately so admins get instant visual confirmation.
      setPending(prev => prev.filter(p => p.id !== id));
      setModerationToast(status === 'approved' ? 'Contribution approved' : 'Contribution rejected');
    } catch (e) {
      console.error("Error updating status:", e);
      setModerationToast("Failed to update status. Please try again.");
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  // MyUploads logic
  const handleItemPress = (item, type) => {
    if (isSelectionMode) {
      toggleItemSelection(item.id);
    } else {
      if (type === "note") {
        setSelectedNote(item);
      } else {
        setSelectedImage(item);
      }
    }
  };

  const handleItemLongPress = (itemId) => {
    setIsSelectionMode(true);
    toggleItemSelection(itemId);
    if (longPressTimer) {
       clearTimeout(longPressTimer);
       setLongPressTimer(null);
    }
  };

  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }

      if (newSet.size === 0) {
          setIsSelectionMode(false);
      } else {
          setIsSelectionMode(true);
      }
      return newSet;
    });
  };

  const deleteNote = async (itemId, uid) => {
    const noteDoc = await getDoc(doc(db, "notes", itemId));
    if (noteDoc.exists() && noteDoc.data().userId === uid) {
      await deleteDoc(doc(db, "notes", itemId));
    }
  };

  const deleteImage = async (itemId, uid) => {
    const imageDoc = await getDoc(doc(db, "images", itemId));
    if (!imageDoc.exists() || imageDoc.data().userId !== uid) return;
    const { url, storagePath } = imageDoc.data();
    // Prefer the stored path; fall back to parsing the download URL for
    // legacy records that pre-date the storagePath field.
    const pathToDelete =
      storagePath ||
      (() => {
        if (url?.includes("firebasestorage.googleapis.com")) {
          const parts = url.split("/o/");
          return parts.length > 1
            ? decodeURIComponent(parts[1].split("?")[0])
            : null;
        }
        return null;
      })();
    if (pathToDelete) {
      await deleteObject(ref(getStorage(), pathToDelete)).catch((e) =>
        console.error("Storage delete failed:", e)
      );
    }
    await deleteDoc(doc(db, "images", itemId));
  };

  const handleDelete = async () => {
    if (selectedItems.size === 0) return;
    setIsDeleting(true);
    try {
      const user = await ensureAuthenticated();
      await Promise.all(
        Array.from(selectedItems).map((itemId) =>
          activeTab === "notes"
            ? deleteNote(itemId, user.uid)
            : deleteImage(itemId, user.uid)
        )
      );
      setSelectedItems(new Set());
      setIsSelectionMode(false);
      fetchContent();
    } catch (err) {
      console.error("Error deleting items:", err);
      setModerationToast("Could not delete. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEdit = async () => {
    if (selectedItems.size !== 1) return;
    
    const itemId = Array.from(selectedItems)[0];
    if (activeTab === "notes") {
      const noteToEdit = notes.find(note => note.id === itemId);
      if (noteToEdit) {
        setSelectedNote(noteToEdit);
        setEditedText(noteToEdit.text);
        setIsEditing(true);
        setSelectedItems(new Set());
        setIsSelectionMode(false);
      }
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedNote || !editedText.trim()) return;
    
    try {
      const user = await ensureAuthenticated();
      const noteRef = doc(db, "notes", selectedNote.id);
      
      const noteDoc = await getDoc(noteRef);
      if (noteDoc.exists() && noteDoc.data().userId === user.uid) {
        await updateDoc(noteRef, {
          text: editedText.trim(),
          updatedAt: new Date()
        });
        
        setNotes(prevNotes => 
          prevNotes.map(note => 
            note.id === selectedNote.id 
              ? { ...note, text: editedText.trim() }
              : note
          )
        );
        
        setSelectedNote(null);
        setEditedText("");
        setIsEditing(false);
      }
    } catch (err) {
      console.error("Error updating note:", err);
      setModerationToast("Could not update note. Please try again.");
    }
  };

  // Zoom gestures
  useEffect(() => {
    const imgEl = imgRef.current;
    if (!imgEl) return;
    let startDist = null;

    const getDistance = (touches) => {
      const [a, b] = touches;
      return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    };

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        startDist = getDistance(e.touches);
      }
    };

    const onTouchMove = (e) => {
      if (e.touches.length === 2 && startDist) {
        const newDist = getDistance(e.touches);
        const newScale = Math.min(Math.max(newDist / startDist, 1), 3);
        setScale(newScale);
      }
    };

    const onTouchEnd = () => {
      startDist = null;
    };

    imgEl.addEventListener("touchstart", onTouchStart);
    imgEl.addEventListener("touchmove", onTouchMove);
    imgEl.addEventListener("touchend", onTouchEnd);

    return () => {
      imgEl.removeEventListener("touchstart", onTouchStart);
      imgEl.removeEventListener("touchmove", onTouchMove);
      imgEl.removeEventListener("touchend", onTouchEnd);
    };
  }, [selectedImage]);

  // ── Touch helpers (shared across both content tabs) ──────────────
  const touchHandlers = (id, type) => ({
    onTouchStart: (e) => {
      touchStartPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      if (longPressTimer) clearTimeout(longPressTimer);
      setLongPressTimer(setTimeout(() => handleItemLongPress(id), 500));
    },
    onTouchMove: (e) => {
      const dx = Math.abs(e.touches[0].clientX - touchStartPos.current.x);
      const dy = Math.abs(e.touches[0].clientY - touchStartPos.current.y);
      if (dx > 10 || dy > 10) { if (longPressTimer) { clearTimeout(longPressTimer); setLongPressTimer(null); } }
    },
    onTouchEnd: () => { if (longPressTimer) { clearTimeout(longPressTimer); setLongPressTimer(null); } },
  });

  return (
    <div className="w-full min-h-screen bg-[var(--color-bg)] font-['Poppins'] text-[#44231E] overflow-x-hidden">

      {/* ── Fixed header ── */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <Header title="My uploads">
          {isAdminLoggedIn ? (
            <Button onClick={() => { setAdminMode(false); signOut(auth); }}>Log out</Button>
          ) : (
            <Button onClick={() => navigate('/admin/login')}>Admin login</Button>
          )}
        </Header>
      </div>

      {/* ── Scrollable body ── */}
      <div
        className="px-6 pb-36"
        style={{ paddingTop: 'max(92px, calc(84px + env(safe-area-inset-top, 0px)))' }}
      >

        {/* ── Admin panel ── */}
        {isAdminLoggedIn && isOwner && (
          <div className="mb-6 space-y-3">

            {/* Admin mode — iOS-style toggle */}
            <div
              className="flex items-center justify-between"
              style={{
                background: '#FFFFFF',
                borderRadius: 18,
                padding: '14px 18px',
                boxShadow: '0 1px 4px rgba(64,42,20,0.07)',
              }}
            >
              <div>
                <p style={{ fontSize: 15, fontWeight: 500, color: '#44231E', fontFamily: 'Poppins', lineHeight: 1.3 }}>
                  Admin mode
                </p>
                <p style={{ fontSize: 12, color: '#B9A68E', fontFamily: 'Poppins', marginTop: 1 }}>
                  {adminMode ? 'Enabled — changes are live' : 'Off — view only'}
                </p>
              </div>
              <button
                role="switch"
                aria-checked={adminMode}
                onClick={() => setAdminMode(!adminMode)}
                style={{
                  position: 'relative',
                  width: 50, height: 28, borderRadius: 100,
                  background: adminMode ? '#44231E' : '#C8BDB4',
                  border: 'none', cursor: 'pointer', flexShrink: 0,
                  transition: 'background 0.2s ease',
                }}
              >
                <motion.span
                  animate={{ x: adminMode ? 24 : 3 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  style={{
                    position: 'absolute', top: 3, left: 0,
                    width: 22, height: 22, borderRadius: '50%',
                    background: '#FFFFFF',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.22)',
                  }}
                />
              </button>
            </div>

            {/* Pending requests */}
            <div>
              <div className="flex items-center justify-between mb-3" style={{ paddingLeft: 2 }}>
                <p style={{ fontSize: 14, fontWeight: 600, color: '#44231E', fontFamily: 'Poppins', letterSpacing: '-0.01em' }}>
                  Pending requests
                </p>
                <span style={{
                  fontSize: 11, fontWeight: 700, color: pending.length > 0 ? '#EFEBE0' : '#B9A68E',
                  background: pending.length > 0 ? '#44231E' : '#E8E0D6',
                  borderRadius: 999, padding: '2px 9px', fontFamily: 'Poppins',
                }}>
                  {pending.length}
                </span>
              </div>

              {pending.length === 0 ? (
                <div style={{
                  background: '#FFFFFF', borderRadius: 16, padding: '16px 18px',
                  display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  <Clock size={16} color="#B9A68E" strokeWidth={1.8} />
                  <p style={{ fontSize: 14, color: '#B9A68E', fontFamily: 'Poppins' }}>No pending submissions</p>
                </div>
              ) : (
                <div
                  className="flex gap-3 overflow-x-auto -mx-6 px-6 pb-2 snap-x"
                  style={{ scrollbarWidth: 'none' }}
                >
                  {pending.map(p => (
                    <div
                      key={p.id}
                      className="snap-center shrink-0 flex flex-col overflow-hidden"
                      style={{
                        width: 244,
                        background: '#FFFFFF',
                        borderRadius: 18,
                        boxShadow: '0 2px 10px rgba(64,42,20,0.09)',
                      }}
                    >
                      {/* Type badge */}
                      <div style={{ padding: '10px 14px 0' }}>
                        <span style={{
                          fontSize: 11, fontWeight: 600, fontFamily: 'Poppins',
                          color: p.type === 'note' ? '#7A4F2D' : '#44231E',
                          background: p.type === 'note' ? '#F5EDE3' : '#EDE5DE',
                          borderRadius: 999, padding: '3px 10px',
                        }}>
                          {p.type === 'note' ? 'Note' : 'Photo'}
                        </span>
                      </div>
                      {/* Content */}
                      {p.type === 'note' ? (
                        <div className="px-4 pt-3 pb-2 flex-1">
                          <p style={{ fontSize: 14, color: '#44231E', lineHeight: 1.6, fontFamily: 'Poppins' }}
                            className="line-clamp-3">{p.text}</p>
                        </div>
                      ) : (
                        <div className="mx-3 mt-3 rounded-xl overflow-hidden" style={{ height: 130, background: '#F0EBE3' }}>
                          {previewUrlsById[p.id]
                            ? <img src={previewUrlsById[p.id]} alt="preview" className="w-full h-full object-cover" />
                            : <div className="w-full h-full flex items-center justify-center">
                                <ImageIcon size={22} color="#C8BDB4" strokeWidth={1.5} />
                              </div>
                          }
                        </div>
                      )}
                      {/* Footer */}
                      <div className="p-3" style={{ borderTop: '1px solid #F0EBE3', marginTop: 8 }}>
                        <div className="flex items-center gap-1.5 mb-2.5">
                          <Icon name="author" size={11} color="#B9A68E" />
                          <span style={{ fontSize: 12, color: '#B9A68E', fontFamily: 'Poppins', fontWeight: 500 }}>
                            {(p.authorName === 'Anonym' ? 'Anonymous' : p.authorName) || 'Anonymous'}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => updateStatus(p.id, 'approved')}
                            disabled={processingIds.has(p.id)}
                            style={{
                              flex: 1, background: '#44231E', color: '#EFEBE0',
                              border: 'none', borderRadius: 100, padding: '8px 0',
                              fontSize: 13, fontFamily: 'Poppins', fontWeight: 500, cursor: 'pointer',
                              opacity: processingIds.has(p.id) ? 0.5 : 1,
                            }}
                          >
                            {processingIds.has(p.id) ? '…' : 'Approve'}
                          </button>
                          <button
                            onClick={() => updateStatus(p.id, 'rejected')}
                            disabled={processingIds.has(p.id)}
                            style={{
                              flex: 1, background: 'white', color: '#8E7C69',
                              border: '1px solid #E8E0D6', borderRadius: 100, padding: '8px 0',
                              fontSize: 13, fontFamily: 'Poppins', fontWeight: 500, cursor: 'pointer',
                              opacity: processingIds.has(p.id) ? 0.5 : 1,
                            }}
                          >
                            {processingIds.has(p.id) ? '…' : 'Reject'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ height: 1, background: '#E8E0D6' }} />
            <p style={{ fontSize: 14, fontWeight: 600, color: '#44231E', fontFamily: 'Poppins', paddingLeft: 2, letterSpacing: '-0.01em' }}>
              My contributions
            </p>
          </div>
        )}

        {/* ── Tab bar ── */}
        <div className="mb-5">
          <div
            className="flex rounded-full"
            style={{
              background: '#FFFFFF',
              padding: 4,
              boxShadow: '0 1px 4px rgba(64,42,20,0.08)',
            }}
          >
            {[
              { id: 'notes',  label: 'Memory notes' },
              { id: 'images', label: 'Pictures' },
            ].map(({ id, label }) => {
              const active = activeTab === id;
              return (
                <motion.button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  whileTap={{ scale: 0.97 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 22 }}
                  className="relative flex-1 focus:outline-none"
                  style={{
                    height: 40,
                    borderRadius: 999,
                    border: 'none',
                    cursor: 'pointer',
                    background: 'transparent',
                    fontFamily: 'Poppins',
                    fontSize: 14,
                    fontWeight: 500,
                    color: active ? '#EFEBE0' : '#8E7C69',
                    zIndex: 1,
                  }}
                >
                  {active && (
                    <motion.span
                      layoutId="myuploads-tab-pill"
                      transition={{ type: 'spring', stiffness: 420, damping: 32 }}
                      className="absolute inset-0 rounded-full"
                      style={{ background: '#44231E' }}
                    />
                  )}
                  <span style={{ position: 'relative', zIndex: 1 }}>{label}</span>
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* ── Content ── */}
        {isLoading ? (
          /* Skeleton — matches the actual content grid shape */
          <div style={{ columnCount: 2, columnGap: 16 }}>
            {[180, 130, 140, 190, 110, 160].map((h, i) => (
              <div key={i} style={{ breakInside: 'avoid', WebkitColumnBreakInside: 'avoid', marginBottom: 16 }}>
                <div className="photocard-shimmer" style={{ height: h, borderRadius: 20 }} />
              </div>
            ))}
          </div>
        ) : error ? (
          <div style={{
            background: '#FFF0EE', borderRadius: 16, padding: '16px 18px',
            display: 'flex', alignItems: 'center', gap: 10, marginTop: 8,
          }}>
            <p style={{ fontSize: 14, color: '#C0392B', fontFamily: 'Poppins' }}>{error}</p>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            {activeTab === 'notes' ? (
              <motion.div
                key="notes"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
              >
                {notes.length === 0 ? (
                  /* Empty state — icon + headline + subtext */
                  <div className="flex flex-col items-center text-center pt-12 pb-8" style={{ gap: 12 }}>
                    <div style={{
                      width: 56, height: 56, borderRadius: 18,
                      background: '#FFFFFF',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 2px 10px rgba(64,42,20,0.09)',
                    }}>
                      <StickyNote size={24} color="#B9A68E" strokeWidth={1.6} />
                    </div>
                    <p style={{ fontSize: 16, fontWeight: 600, color: '#44231E', fontFamily: 'Poppins' }}>
                      No notes yet
                    </p>
                    <p style={{ fontSize: 14, color: '#B9A68E', fontFamily: 'Poppins', maxWidth: 220, lineHeight: 1.5 }}>
                      Memory notes you write will show up here
                    </p>
                  </div>
                ) : (
                  <div style={{ columnCount: 2, columnGap: 16, width: '100%' }}>
                    {notes.map((note, i) => (
                      <motion.div
                        key={note.id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2, delay: Math.min(i * 0.04, 0.2), ease: 'easeOut' }}
                        onClick={() => handleItemPress(note, 'note')}
                        {...touchHandlers(note.id, 'note')}
                        style={{ breakInside: 'avoid', WebkitColumnBreakInside: 'avoid', marginBottom: 16 }}
                      >
                        <MemoryNoteCard
                          variant="grid"
                          date={formatDate(note.createdAt)}
                          body={note.text}
                          author={note.author || 'Anonymous'}
                          maxWords={16}
                          className={selectedItems.has(note.id) ? 'ring-2 ring-[#44231E]' : ''}
                        />
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="images"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
              >
                {images.length === 0 ? (
                  /* Empty state — icon + headline + subtext */
                  <div className="flex flex-col items-center text-center pt-12 pb-8" style={{ gap: 12 }}>
                    <div style={{
                      width: 56, height: 56, borderRadius: 18,
                      background: '#FFFFFF',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 2px 10px rgba(64,42,20,0.09)',
                    }}>
                      <ImageIcon size={24} color="#B9A68E" strokeWidth={1.6} />
                    </div>
                    <p style={{ fontSize: 16, fontWeight: 600, color: '#44231E', fontFamily: 'Poppins' }}>
                      No photos yet
                    </p>
                    <p style={{ fontSize: 14, color: '#B9A68E', fontFamily: 'Poppins', maxWidth: 220, lineHeight: 1.5 }}>
                      Photos you upload will appear here
                    </p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                    {images.map((image, i) => (
                      <motion.div
                        key={image.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.2, delay: Math.min(i * 0.04, 0.2), ease: 'easeOut' }}
                        onClick={() => handleItemPress(image, 'image')}
                        {...touchHandlers(image.id, 'image')}
                        style={{
                          height: 180, background: '#F0EBE3', borderRadius: 18,
                          overflow: 'hidden', cursor: 'pointer', position: 'relative',
                          boxShadow: selectedItems.has(image.id)
                            ? '0 0 0 2.5px #44231E'
                            : '0 2px 8px rgba(64,42,20,0.08)',
                          userSelect: 'none', WebkitUserSelect: 'none',
                        }}
                      >
                        <img src={image.url} alt="Uploaded" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <div style={{
                          position: 'absolute', bottom: 0, left: 0, right: 0,
                          padding: '24px 10px 8px',
                          background: 'linear-gradient(to top, rgba(30,14,6,0.50) 0%, transparent 100%)',
                        }}>
                          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.88)', fontFamily: 'Poppins', fontWeight: 500 }}>
                            {formatDate(image.createdAt)}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>

      {/* ── Selection action bar ── */}
      <AnimatePresence>
        {selectedItems.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ type: 'spring', stiffness: 340, damping: 28 }}
            className="fixed left-1/2 -translate-x-1/2 z-40 flex items-center gap-3"
            style={{ bottom: 108 }}
          >
            <Button onClick={handleEdit} disabled={!(activeTab === 'notes' && selectedItems.size === 1)}>
              Edit
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? 'Deleting…' : `Delete ${selectedItems.size}`}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Moderation feedback toast ── */}
      <AnimatePresence>
        {moderationToast && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="fixed left-1/2 -translate-x-1/2 z-40"
            style={{ bottom: 108 }}
          >
            <div
              style={{
                padding: '10px 14px',
                borderRadius: 999,
                background: '#44231E',
                color: '#EFEBE0',
                fontSize: 13,
                fontFamily: 'Poppins',
                fontWeight: 500,
                boxShadow: '0 6px 18px rgba(64,42,20,0.22)',
              }}
            >
              {moderationToast}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Note viewer modal ── */}
      <AnimatePresence>
        {!isSelectionMode && selectedNote && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center px-5"
            style={{ background: 'rgba(20,10,4,0.55)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
            onClick={(e) => {
              if (e.target === e.currentTarget && !isEditing) {
                setSelectedNote(null);
                setEditedText("");
              }
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white w-full max-w-[380px] max-h-[80vh] flex flex-col"
              style={{ borderRadius: 24, overflow: 'hidden' }}
              initial={{ scale: 0.94, opacity: 0, y: 12 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.94, opacity: 0, y: 12 }}
              transition={{ type: 'spring', damping: 26, stiffness: 320 }}
            >
              {/* Modal header */}
              <div className="flex items-center justify-between flex-shrink-0" style={{ padding: '14px 14px 14px 20px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                <div className="flex items-center gap-2">
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#E09053', flexShrink: 0 }} />
                  <p style={{ fontSize: 12, color: '#B9A68E', fontFamily: 'Poppins' }}>{formatDate(selectedNote.createdAt)}</p>
                </div>
                <button
                  onClick={() => {
                    if (isEditing) {
                      setDiscardConfirm(true);
                    } else {
                      setSelectedNote(null);
                      setEditedText("");
                    }
                  }}
                  style={{
                    width: 44, height: 44, borderRadius: 999, border: 'none', cursor: 'pointer',
                    background: 'rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <X size={15} color="#6F6F6F" strokeWidth={2} />
                </button>
              </div>

              {/* Note body */}
              <div className="flex-1 overflow-y-auto min-h-0" style={{ padding: '20px 20px 8px' }}>
                {isEditing ? (
                  <textarea
                    value={editedText}
                    onChange={(e) => setEditedText(e.target.value)}
                    style={{
                      width: '100%', minHeight: 200, resize: 'none',
                      border: 'none', outline: 'none', background: 'transparent',
                      fontFamily: 'Poppins', fontSize: 16, fontWeight: 400, lineHeight: 1.6,
                      color: '#44231E',
                    }}
                    placeholder="Edit your note…"
                    autoFocus
                  />
                ) : (
                  String(selectedNote.text || '').split(/\n+/).filter(Boolean).map((p, idx, arr) => (
                    <p key={idx} style={{ fontSize: 16, fontFamily: 'Poppins', color: '#44231E', lineHeight: 1.65, marginBottom: idx === arr.length - 1 ? 0 : 14 }}>
                      {p}
                    </p>
                  ))
                )}
              </div>

              {/* Modal footer */}
              <div className="flex-shrink-0 flex items-center justify-between" style={{ padding: '12px 16px 14px 20px', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                <div className="flex items-center gap-2" style={{ padding: '5px 10px', background: 'rgba(0,0,0,0.04)', borderRadius: 999 }}>
                  <Icon name="author" size={12} color="#939393" />
                  <p style={{ fontSize: 12, color: '#939393', fontFamily: 'Poppins', fontWeight: 500 }}>
                    {selectedNote.author || 'Anonymous'}
                  </p>
                </div>
                {isEditing
                  ? <Button onClick={handleSaveEdit}>Save changes</Button>
                  : <Button onClick={() => { setEditedText(selectedNote.text); setIsEditing(true); }}>Edit</Button>
                }
              </div>

              {/* Discard confirmation — shown instead of window.confirm */}
              {discardConfirm && (
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center gap-3"
                  style={{ background: 'rgba(255,255,255,0.96)', borderRadius: 24 }}
                >
                  <p style={{ fontSize: 15, fontFamily: 'Poppins', color: '#44231E', fontWeight: 500 }}>
                    Discard unsaved changes?
                  </p>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => {
                        setDiscardConfirm(false);
                        setSelectedNote(null);
                        setIsEditing(false);
                        setEditedText("");
                      }}
                    >
                      Discard
                    </Button>
                    <Button onClick={() => setDiscardConfirm(false)}>Keep editing</Button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Image viewer modal ── */}
      {!isSelectionMode && selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(10,5,2,0.85)' }}
          onClick={(e) => { if (e.target === e.currentTarget) { setSelectedImage(null); setScale(1); } }}
        >
          <button
            onClick={() => { setSelectedImage(null); setScale(1); }}
            className="absolute z-10 pointer-events-auto"
            style={{
              top: 16, right: 16, width: 44, height: 44, borderRadius: 999,
              background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.20)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            }}
          >
            <X size={16} color="#FFFFFF" strokeWidth={2} />
          </button>
          <motion.img
            ref={imgRef}
            src={selectedImage.url}
            alt="Fullscreen"
            className="pointer-events-auto"
            style={{ maxHeight: '90vh', maxWidth: '90vw', borderRadius: 12, transform: `scale(${scale})` }}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          />
        </div>
      )}

    </div>
  );
}